import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

// The `demo-*` form styles (demo-form, demo-row, demo-field, demo-submit, …)
// this page renders live in demo_page.css. They used to reach the bundle via
// demo_page.js; that page was retired, so this page imports them directly.
import "./demo_page.css";
import "./contact_us_page.css";

import { trackClick } from "../telemetry";
import Logo from "../assets/composiq-mark-128.png";

const contactIntents = {
  "early-access": {
    title: "Request Early Access Deployment",
    intro: "Tell us about your Azure environment, compliance requirements, and target deployment timeline.",
    reason: "Early access deployment",
    messagePlaceholder: "Share your target timeline, Azure tenant status, and the first use case you want to launch.",
    reassurance: "We respond within 1 business day with next steps for deployment fit."
  },
  "first-agent": {
    title: "Your First Agent",
    intro: "Tell us about the workflow you want your first agent to handle. We'll scope it together and give you an honest read on whether we can build it in 4 weeks.",
    reason: "Early access deployment",
    messagePlaceholder: "Describe the workflow, the data sources involved, and what 'good' looks like for your team.",
    reassurance: "We respond within 1 business day with a scoping outline."
  },
  "security-review": {
    title: "See If Zerivo Fits",
    intro: "Tell us about your environment, review process, and first use case. We will help you decide whether Zerivo is a good fit.",
    reason: "Fit conversation",
    messagePlaceholder: "Share your Azure setup, review process, or the first workflow you want to make private.",
    reassurance: "We respond within 1 business day with practical next steps."
  },
  "fit": {
    title: "See If Zerivo Fits",
    intro: "Tell us about your environment, review process, and first use case. We will help you decide whether Zerivo is a good fit.",
    reason: "Fit conversation",
    messagePlaceholder: "Share your Azure setup, review process, or the first workflow you want to make private.",
    reassurance: "We respond within 1 business day with practical next steps."
  },
  "security-packet": {
    title: "See If Zerivo Fits",
    intro: "Tell us about your environment, review process, and first use case. We will help you decide whether Zerivo is a good fit.",
    reason: "Fit conversation",
    messagePlaceholder: "Share your Azure setup, review process, or the first workflow you want to make private.",
    reassurance: "We respond within 1 business day with practical next steps."
  },
  "ciso-architecture": {
    title: "Architecture & Zero-Egress Walkthrough",
    intro: "Tell us about your Azure tenant and review process. We'll walk you through the architecture and zero-egress verification before asking for a meeting.",
    reason: "Fit conversation",
    messagePlaceholder: "Share your Azure setup, network constraints, and what your security review needs to see.",
    reassurance: "We respond within 1 business day with the architecture packet and next steps."
  },
  "compliance-ledger": {
    title: "Audit Ledger Walkthrough",
    intro: "Tell us about your compliance framework and audit requirements. We'll walk you through how the tamper-evident ledger maps to your controls.",
    reason: "Fit conversation",
    messagePlaceholder: "Share your compliance framework (FedRAMP, HIPAA, SOC 2, etc.), retention needs, and the controls your auditors want evidence for.",
    reassurance: "We respond within 1 business day with the audit ledger packet and next steps."
  }
};

const getContactIntent = (search) => {
  const params = new URLSearchParams(search);
  const key = params.get("intent") || params.get("type") || "";
  const intent = contactIntents[key];
  if (!intent) return null;
  return { ...intent, sourceSlug: key };
};

export default function ContactUsPage(){
  const [mobileOpen, setMobileOpen] = useState(false);

  /* ── Header gains its hairline once the page is scrolled ── */
  const [headerScrolled, setHeaderScrolled] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const update = () => { raf = 0; setHeaderScrolled(window.scrollY > 4); };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const contactIntent = useMemo(() => getContactIntent(location.search), [location.search]);
  const formRef = useRef(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    country: "United States",
    phone: "",
    reason: contactIntent?.reason || "",
    message: ""
  });

  const [touched, setTouched] = useState({});
  const [showPhone, setShowPhone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const markTouched = (key) => () => setTouched((prev) => ({ ...prev, [key]: true }));

  useEffect(() => {
    if (!contactIntent?.reason) return;
    setForm((prev) => {
      if (prev.reason && prev.reason !== "General question") return prev;
      return { ...prev, reason: contactIntent.reason };
    });
  }, [contactIntent]);

  const fieldErrors = useCallback(() => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required.";
    if (!form.lastName.trim()) errs.lastName = "Last name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (!form.reason) errs.reason = "Please select a reason.";
    return errs;
  }, [form]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;

    const errs = fieldErrors();
    if (Object.keys(errs).length) {
      const allTouched = {};
      Object.keys(errs).forEach((k) => { allTouched[k] = true; });
      setTouched((prev) => ({ ...prev, ...allTouched }));
      const firstKey = Object.keys(errs)[0];
      const el = formRef.current?.querySelector(`[data-field="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    setError("");
    let timeoutId;
    try {
      const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || "";

      const controller = new AbortController();
      // 45s tolerates Azure App Service cold starts (first hit after a restart
      // can take 30-60s before the Node process accepts traffic).
      timeoutId = setTimeout(() => controller.abort(), 45000);

      const res = await fetch(`${API_BASE}/api/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          intent: contactIntent?.title || "General contact",
          intent_source: contactIntent?.sourceSlug || ""
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      navigate("/thanks-contact", { state: { firstName: form.firstName } });
    } catch (err) {
      const msg = err?.name === "AbortError"
        ? "Request timed out. Please try again."
        : (err?.message || "Something went wrong. Please try again.");
      setError(msg);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setSubmitting(false);
    }
  };

  const errs = fieldErrors();

  return (
    <div className="App contact-page">
      {/* ───────── HEADER (match Home) ───────── */}
      <header className={`dbx-header${headerScrolled ? ' is-scrolled' : ''}`} role="banner">
        <div className="dbx-header-inner">
          <Link to="/" className="dbx-brand" aria-label="Zerivo home">
            <img className="dbx-brand-mark" src={Logo} alt="" />
            <div className="dbx-brand-text-group">
              <span className="dbx-brand-text">Zerivo</span>
              <span className="dbx-brand-subtitle">Private AI</span>
            </div>
          </Link>

          <div className="dbx-header-right">
            <nav className="dbx-header-nav" aria-label="Main navigation">
              <Link to="/our-team">Our Team</Link>
              <Link to="/contact">Contact</Link>
            </nav>

            <div className="dbx-header-actions">
              <a
                href="https://portal.azure.com/#view/Microsoft_Azure_Marketplace/MarketplaceOffersBlade/selectedMenuItemId/home"
                className="dbx-btn header-cta"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("Get Zerivo on Azure", { placement: "header", page: "contact" })}
              >
                Get Zerivo on Azure
              </a>
            </div>
          </div>

        </div>
      </header>

      {mobileOpen && (
        <nav className="dbx-mobile-nav" role="navigation" aria-label="Mobile navigation">
          <button className="dbx-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">×</button>
          <Link to="/our-team" onClick={() => setMobileOpen(false)}>Our Team</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link to="/privacy" onClick={() => setMobileOpen(false)}>Privacy</Link>
          <Link to="/terms" onClick={() => setMobileOpen(false)}>Terms</Link>
        </nav>
      )}
      {mobileOpen && <div className="dbx-mobile-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true"></div>}

      {/* ───────── MAIN CONTENT ───────── */}
      <main className="contact-wrapper">
        <h1 className="contact-title">{contactIntent?.title || "Contact Us"}</h1>
        <p className="contact-intro">
          {contactIntent?.intro || "Fill out the form and a team member will reach out."}
        </p>

        {contactIntent && (
          <div className="contact-intent-panel" aria-label="What happens next">
            <div className="contact-intent-title">What happens next</div>
            <ul>
              <li>We confirm your buying stage and primary reviewer.</li>
              <li>We send the right proof artifacts before asking for a meeting.</li>
              <li>If there is a fit, we schedule a short architecture walkthrough.</li>
            </ul>
          </div>
        )}

        {error && (
          <div className="demo-toast error" role="alert">{error}</div>
        )}

        <div className="contact-card" ref={formRef}>
          <form className="demo-form" onSubmit={submit} noValidate>
            {/* Row 1: Name */}
            <div className="demo-row">
              <div className="demo-field" data-field="firstName">
                <label htmlFor="contact-firstName">First Name <span className="demo-req">*</span></label>
                <input
                  id="contact-firstName"
                  type="text"
                  value={form.firstName}
                  onChange={update("firstName")}
                  onBlur={markTouched("firstName")}
                  autoComplete="given-name"
                  required
                />
                {touched.firstName && errs.firstName && <span className="demo-error">{errs.firstName}</span>}
              </div>
              <div className="demo-field" data-field="lastName">
                <label htmlFor="contact-lastName">Last Name <span className="demo-req">*</span></label>
                <input
                  id="contact-lastName"
                  type="text"
                  value={form.lastName}
                  onChange={update("lastName")}
                  onBlur={markTouched("lastName")}
                  autoComplete="family-name"
                  required
                />
                {touched.lastName && errs.lastName && <span className="demo-error">{errs.lastName}</span>}
              </div>
            </div>

            {/* Row 2: Company + Email */}
            <div className="demo-row">
              <div className="demo-field">
                <label htmlFor="contact-company">Company</label>
                <input
                  id="contact-company"
                  type="text"
                  value={form.company}
                  onChange={update("company")}
                  autoComplete="organization"
                />
              </div>
              <div className="demo-field" data-field="email">
                <label htmlFor="contact-email">Work Email <span className="demo-req">*</span></label>
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  onBlur={markTouched("email")}
                  autoComplete="email"
                  required
                />
                <span className="demo-helper">Use a work email for fastest response.</span>
                {touched.email && errs.email && <span className="demo-error">{errs.email}</span>}
              </div>
            </div>

            {/* Row 3: Country + Reason */}
            <div className="demo-row">
              <div className="demo-field">
                <label htmlFor="contact-country">Country</label>
                <select id="contact-country" value={form.country} onChange={update("country")}>
                  <option>United States</option>
                  <option>Canada</option>
                  <option>United Kingdom</option>
                  <option>Australia</option>
                  <option>Germany</option>
                  <option>France</option>
                  <option>India</option>
                  <option>Singapore</option>
                  <option>Japan</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="demo-field" data-field="reason">
                <label htmlFor="contact-reason">Contact Reason <span className="demo-req">*</span></label>
                <select id="contact-reason" value={form.reason} onChange={update("reason")} onBlur={markTouched("reason")} required>
                  <option value="">Select…</option>
                  <option>Early access deployment</option>
                  <option>Design partner inquiry</option>
                  <option>Fit conversation</option>
                  <option>Procurement / pricing question</option>
                  <option>General question</option>
                </select>
                {touched.reason && errs.reason && <span className="demo-error">{errs.reason}</span>}
              </div>
            </div>

            {/* Optional: Phone */}
            {!showPhone ? (
              <button type="button" className="demo-toggle-optional" onClick={() => setShowPhone(true)}>
                + Add phone number <span className="demo-opt-tag">(optional)</span>
              </button>
            ) : (
              <div className="demo-field">
                <label htmlFor="contact-phone">Phone Number <span className="demo-opt">(optional)</span></label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  autoComplete="tel"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            )}

            {/* Message */}
            <div className="demo-field">
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                rows="4"
                value={form.message}
                onChange={update("message")}
                placeholder={contactIntent?.messagePlaceholder || "Tell us about your needs, timeline, or questions."}
              />
            </div>

            <p className="demo-consent">By submitting, I agree to the processing of my personal data by <strong>Zerivo</strong> in accordance with our <Link to="/privacy">privacy policy</Link>.</p>
            <p className="demo-reassure">{contactIntent?.reassurance || "We respond within 1 business day."}</p>

            <div className="demo-actions">
              <button
                className="demo-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="demo-spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
